import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  RefreshCw, Search, Eye, Activity,
  CheckCircle2, AlertTriangle, Clock3, X,
} from "lucide-react";
import { getWebhookLogs } from "../services/webhookLogs";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";

function formatDate(value) {
  if (!value) return "—";
  try { return format(new Date(value), "dd/MM/yyyy HH:mm"); }
  catch { return "—"; }
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const n = (status || "").toLowerCase();
  const map = {
    processed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    success:   "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    pending:   "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    error:     "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400",
    failed:    "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400",
  };
  const iconMap = {
    processed: <CheckCircle2 className="w-3.5 h-3.5" />,
    success:   <CheckCircle2 className="w-3.5 h-3.5" />,
    pending:   <Clock3       className="w-3.5 h-3.5" />,
    error:     <AlertTriangle className="w-3.5 h-3.5" />,
    failed:    <AlertTriangle className="w-3.5 h-3.5" />,
  };
  const cls = map[n] || "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {iconMap[n] || <Activity className="w-3.5 h-3.5" />}
      {status || "—"}
    </span>
  );
}

// ─── Detail Item ──────────────────────────────────────────────────────────────

function DetailItem({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2 text-sm text-slate-800 dark:text-slate-200">
        {value || "—"}
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function LogDetailsModal({ open, onClose, log }) {
  if (!open || !log) return null;
  let rawFormatted = "—";
  try { rawFormatted = typeof log.raw_data === "string" ? log.raw_data : JSON.stringify(log.raw_data, null, 2); }
  catch { rawFormatted = String(log.raw_data || "—"); }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Detalhes do log</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Dados completos do webhook recebido.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <DetailItem label="Data"   value={formatDate(log.created_at)} />
            <DetailItem label="Origem" value={log.origin} />
            <DetailItem label="Status" value={log.status} />
            <DetailItem label="Pedido" value={log.order_id || log.orderid || log.transaction_id || "—"} />
          </div>
          <DetailItem label="Mensagem" value={log.message} />
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Payload bruto</p>
            <pre className="max-h-[360px] overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 text-xs leading-6 text-slate-800 dark:text-slate-200">
              {rawFormatted}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function WebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchLogs = async () => {
    setLoading(true); setErro("");
    try {
      const data = await getWebhookLogs();
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) { setErro(e.message || "Erro ao buscar logs"); setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  const filteredLogs = useMemo(() => logs.filter((log) => {
    const q = query.trim().toLowerCase();
    const matchesQuery = !q ||
      String(log.origin  || "").toLowerCase().includes(q) ||
      String(log.status  || "").toLowerCase().includes(q) ||
      String(log.message || "").toLowerCase().includes(q) ||
      String(log.order_id || log.orderid || log.transaction_id || "").toLowerCase().includes(q);
    const matchesStatus = !statusFilter || String(log.status || "").toLowerCase() === statusFilter.toLowerCase();
    return matchesQuery && matchesStatus;
  }), [logs, query, statusFilter]);

  const openDetails = (log) => { setSelectedLog(log); setModalOpen(true); };

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Activity className="w-6 h-6" /> Logs de Webhook
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Acompanhe os webhooks recebidos e identifique falhas de processamento.
            </p>
          </div>
          <button onClick={fetchLogs} disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{loading ? "Atualizando..." : "Atualizar"}</span>
          </button>
        </div>

        {/* FILTROS */}
        <div className="grid gap-3 md:grid-cols-[1fr_200px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Buscar por origem, status, pedido ou mensagem..."
              value={query} onChange={(e) => setQuery(e.target.value)}
              className={`${inputCls} pl-9`} />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            <option value="">Todos os status</option>
            <option value="processed">Processed</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {/* TABELA */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          {loading ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">Carregando logs...</p>
          ) : erro ? (
            <p className="p-6 text-sm text-red-600 dark:text-red-400">Erro: {erro}</p>
          ) : filteredLogs.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">Nenhum log encontrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700">
                  <tr className="text-left">
                    {["Data","Origem","Pedido","Status","Mensagem","Ações"].map((h, i) => (
                      <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 ${i === 5 ? "text-center" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{log.origin || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{log.order_id || log.orderid || log.transaction_id || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={log.status} /></td>
                      <td className="px-4 py-3 max-w-[420px]">
                        <div className="line-clamp-2 break-words text-slate-600 dark:text-slate-400">{log.message || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center">
                          <button onClick={() => openDetails(log)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-medium">
                            <Eye className="w-3.5 h-3.5" /> Ver
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <LogDetailsModal open={modalOpen} onClose={() => setModalOpen(false)} log={selectedLog} />
    </div>
  );
}